import { TeApiContext } from '@ecommerce/nest-ssr/common';
import Circle from 'atomic/circle/circle';
import Icon from 'atomic/icon';
import Link from 'atomic/link';
import Price from 'atomic/price';
import Rating from 'atomic/rating/rating';
import classNames from 'classnames';
import DealerOpinionsInterface, { BadgeType } from 'interfaces/dealer-opinions.interface';
import OfferAvailabilityInterface from 'interfaces/offer-availability.interface';
import ProducerInterface from 'interfaces/producer.interface';
import React, { useContext, useState, ReactNode } from 'react';
import DealerSmallBadges from './components/dealer-small-badges';
import Delivery from './components/delivery/delivery';
import OfferDealerInfo from './product-offer-dealer-info';
import ShippingsInterface from 'interfaces/shippings.interface';
import DealerBadgeInterface from 'interfaces/dealer-badge.interface';
import Rates from 'modules/product/views/components/product-offers/components/rates';

import './product-offer.scss';

interface ProductOfferProps {
    id: number;
    shopUrl: string;
    highlighted?: boolean;
    opinions: DealerOpinionsInterface | null;
    image: string;
    title: string;
    badges: {
        fastPayment?: boolean;
        isOpineoQuality?: boolean;
        isQuality?: boolean;
        inPost?: boolean;
        freeDelivery?: DealerBadgeInterface;
        delivery24?: boolean;
        promoText?: string;
    };
    rates?: DealerBadgeInterface;
    price: number;
    isOpen?: boolean;
    variantsCount?: number;
    dealer: {
        id: number;
        name: string;
        logo: string | null;
        authorized: {
            logo: string | null;
        };
        badge?: {
            name: string;
            type: BadgeType;
        };
        producer?: ProducerInterface;
        shippings?: ShippingsInterface[];
        opinions: DealerOpinionsInterface | null;
    };
    availability: OfferAvailabilityInterface | null;
    toggleOpenVariants: () => void;
    starsSize: string;
}

const ProductOffer: React.FC<ProductOfferProps> = props => {
    const api = useContext(TeApiContext);
    const gtmTag = props.availability
        ? props.availability.attributes
            .find((attr: { label: string; value: string; }) => attr.label === 'gtmTag')?.value
        : '';

    if (api === null) {
        throw new Error('Invalid API context');
    }

    const shopUrl = `${props.shopUrl}&badge=1`;
    const [isOpenDealerInfo, setIsOpenDealerInfo] = useState<boolean>(false);
    const [isOpenVariants, setIsOpenVariants] = useState<boolean>(false);

    const onToggleOpenDealerInfo = (): void => {
        setIsOpenDealerInfo(!isOpenDealerInfo);
    };

    const onToggleOpenVariants = (): void => {
        setIsOpenVariants(!isOpenVariants);
        props.toggleOpenVariants();
    };

    const renderAvailability = (): ReactNode => {
        if (props.availability) {
            return (
                <span className="product-offer__badges-item">
                    <Icon color="primary" name="clock" size={14} />
                    {' '}
                    {props.availability.label}
                </span>
            );
        }

        if (props.badges.delivery24) {
            return (
                <span className="product-offer__badges-item">
                    <Icon color="primary" name="clock" size={14} />
                    {' Wysyłka w 24h'}
                </span>
            );
        }

        return (
            <a
                className="product-offer__badges-item product-offer__badges-availability"
                data-gtm={gtmTag}
                href={shopUrl}
                target="_blank"
                rel="nofollow noopener noreferrer"
                aria-label="Idź do sklepu"
            >
                <Icon color="primary" name="clock" size={14} />
                {' Sprawdź w sklepie'}
            </a>
        );
    };

    return (<>
        <div className={classNames({
            'product-offer': true,
            'product-offer--promo-text-hidden': !props.badges.promoText,
            'gtm_or_bs': true,
            'gtm_bdg_h product-offer--highlighted': props.highlighted
        })}
        >
            <a
                href={shopUrl}
                className="product-offer__link gtm_or_row"
                target="_blank"
                rel="nofollow noopener noreferrer"
                aria-label="Idź do sklepu"
                data-cy="list-go-to-shop"
            />
            <div className="product-offer__logo_and_rating">
                <div className="product-offer__logo_and_rating__logo">
                    {props.dealer.logo
                        ? <img
                            className="product-offer__logo_and_rating__logo__img"
                            src={props.dealer.logo}
                            alt={props.dealer.name}
                            width={100}
                            height={40}
                            loading="lazy"
                        />
                        : (
                            <div className="product-offer__logo_and_rating__logo__text">
                                {props.dealer.name}
                            </div>
                        )}
                </div>
                <div className="product-offer__logo_and_rating__rating">
                    <div className="gtm_stars">
                        <Rating className="offer-stars" opinions={props.opinions} size="md" />
                    </div>
                </div>
            </div>
            <div className="product-offer__title">
                <span className="product-offer__title-text gtm_or_name" title={props.title}>
                    {props.title}
                </span>
            </div>
            {props.badges.promoText && (
                <span className="product-offer__promo-text">
                    {props.badges.promoText}
                </span>
            )}
            <DealerSmallBadges className="product-offer__dealer-badges" dealerName={props.dealer.name} dealerBadge={props.dealer.badge}
                authorizedConfig={props.dealer.producer?.config.badges.authorized} isOpineoQuality={props.badges.isOpineoQuality}
            />

            <span className="product-offer__badges">
                <Delivery
                    offerDescription={props.title}
                    className="product-offer__badges-item product-offer__badges-item--delivery gtm_bdg_fd"
                    freeDelivery={props.badges.freeDelivery}
                    dealerId={props.dealer.id}
                />
                {props.badges.fastPayment && (
                    <span className="product-offer__badges-item gtm_bdg_fp">
                        <Icon color="primary" name="credit-card" size={14} />
                        {' '}
                        Szybkie płatności
                    </span>
                )}
                {renderAvailability()}
                {props.badges.inPost && (
                    <span className="product-offer__badges-item">
                        <Icon color="primary" name="inpost" size={14} />
                        {' '}
                        Wysyłka z inPost
                    </span>
                )}
                {props.dealer.shippings?.some(item => item.service === 'Odbiór osobisty') && (
                    <span className="product-offer__badges-item">
                        <Icon color="primary" name="map-pin" size={14} />
                        {' '}
                        Odbiór osobisty
                    </span>
                )}
                <Rates
                    className="product-offer__badges-item gtm_bdg_r"
                    offerDescription={props.title}
                    rates={props.rates}
                />
            </span>

            <div className="product-offer__pricing-and-cta">
                <Price color="secondary" price={props.price} weight={600}
                    fontSize={18}
                    className="product-offer__pricing-and-cta__price"
                />
                <Delivery
                    offerDescription={props.title}
                    freeDelivery={props.badges.freeDelivery}
                    dealerId={props.dealer.id}
                    className="product-offer__pricing-and-cta__delivery gtm_bdg_fd"
                />
                <Link
                    href={shopUrl}
                    target="_blank"
                    className="product-offer__pricing-and-cta__button"
                    type={'button'}
                    color="primary"
                    block={true}
                    rel="nofollow noopener noreferrer"
                    ariaLabel="Idź do sklepu"
                >
                    <div className="text gtm_or_button">
                        Idź do sklepu
                    </div>
                    <Icon
                        className="product-offer__pricing-and-cta__button__icon"
                        name="arrow-right"
                        size={18}
                        color="white"
                    />
                </Link>
            </div>

            <div className="product-offer__expanders">
                {props.variantsCount
                    ? (
                        <Circle
                            className={classNames({
                                'product-offer__expanders-variants': true,
                                'gtm_oa_ver': true,
                                'product-offer__expanders-variants-over-ten': props.variantsCount > 9
                            })}
                            tag="div"
                            background={isOpenVariants ? 'secondary' : 'transparent'}
                            color={isOpenVariants ? 'white' : 'secondary'}
                            border="secondary"
                            size={32}
                            title={isOpenVariants ? 'Ukryj warianty' : 'Pokaż warianty'}
                            ariaLabel="pozostałe warianty"
                            onClick={(event): void => {
                                event.preventDefault();
                                event.stopPropagation();
                                onToggleOpenVariants();
                            }}
                        >
                            +{props.variantsCount}
                        </Circle>
                    )
                    : ''}

                <Circle
                    className="product-offer__expanders-variants gtm_oa_ver"
                    tag="div"
                    background={isOpenDealerInfo ? 'secondary' : 'transparent'}
                    border="secondary"
                    size={32}
                    onClick={(event): void => {
                        event.preventDefault();
                        event.stopPropagation();
                        onToggleOpenDealerInfo();
                    }}
                >
                    <Icon name={isOpenDealerInfo ? 'chevron-up' : 'chevron-down'} color={isOpenDealerInfo ? 'white' : 'secondary'} size={20} />
                </Circle>

            </div>
        </div>
        {isOpenDealerInfo && <OfferDealerInfo className="offer-wrapper-dealer-info"
            dealer={{
                id: props.dealer.id,
                name: props.dealer.name,
                authorized: props.dealer.authorized,
                badge: props.dealer.opinions?.badges.badge,
                opinions: props.dealer.opinions,
                producer: props.dealer.producer
            }}
            badges={{
                fastPayment: Boolean(props.badges.fastPayment),
                isQuality: props.dealer.opinions?.badges.qsign,
                isOpineoQuality: props.dealer.opinions?.badges.credibilityVerification,
                inPost: Boolean(props.badges.inPost),
                freeDelivery: Boolean(props.badges.freeDelivery),
                delivery24: Boolean(props.badges.delivery24),
                promoText: props.badges.promoText
            }}
            title={props.title}
            rates={Boolean(props.rates)}
        />
        }
    </>);
};

export default ProductOffer;
